import React, { useState, useCallback, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Grid } from '@mui/material';
import { gridSize } from '@/shared';

interface CustomAutocompleteFetchProps<T> {
  label?: string;
  fetchData: (searchValue: string, page: number) => Promise<{ options: T[]; hasMore: boolean }>;
  getOptionLabel: (option: T) => string;
  onChangeValue: (value: T | null) => void;
  initialValue?: string;
  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  disabled?: boolean
}

function CustomAutocompleteFetch<T>({
  label = 'Seleccionar',
  fetchData,
  getOptionLabel,
  onChangeValue,
  initialValue = '',
  size = gridSize,
  disabled
}: CustomAutocompleteFetchProps<T>) {
  const [search, setSearch] = useState(initialValue);
  const [options, setOptions] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      setLoading(true);
      const { options: newOptions, hasMore } = await fetchData(search, 1);
      setOptions(newOptions);
      setPage(1);
      setHasMore(hasMore);
      setLoading(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [search]);

  const loadMoreOptions = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    const nextPage = page + 1;
    const { options: newOptions, hasMore: more } = await fetchData(search, nextPage);
    setOptions((prev) => [...prev, ...newOptions]);
    setPage(nextPage);
    setHasMore(more);
    setLoading(false);
  }, [loading, hasMore, page, search]);

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, newInputValue: string) => {
      setSearch(newInputValue);
    },
    []
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, value: T | null) => {
      onChangeValue(value ?? null);
    },
    [onChangeValue]
  );

  return (
    <Grid item {...size}>

      <Autocomplete
        //freeSolo
        noOptionsText={'No hay opciones'}
        options={options}
        loading={loading}
        getOptionLabel={(option) => getOptionLabel(option)}
        popupIcon={null}
        onInputChange={handleInputChange}
        onChange={handleChange}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            autoComplete="off"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          onScroll: (event: React.UIEvent<HTMLElement>) => {
            const listboxNode = event.currentTarget;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight >=
              listboxNode.scrollHeight - 1
            ) {
              loadMoreOptions();
            }
          },
        }}
      />
    </Grid>
  );
}

export default CustomAutocompleteFetch;
